import { useEffect, useState } from 'react';

import Fingering from '../../components/Fingering/Fingering';
import CoinDrops from '../../components/Idle/CoinDrops';
import Instrument from '../../components/Instrument/Instrument';
import MusicNote from '../../components/MusicNote/MusicNote';
import Options from '../../components/Options/Options';
import { awardCorrect, getIdleState, recordWrong, setSetting } from '../../lib/idle/idleStore';
import { notes } from '../../utils/Dictionaries/brassNotes';
import { notesWithoutFlats } from '../../utils/Dictionaries/brassNotesWithoutFlats';
import { getCombos, getDefaultType } from '../../utils/Dictionaries/instrumentTypes';
import { keySignatures } from '../../utils/Dictionaries/keySignatures';
import {
  fingeringType,
  resolveNotation,
  sampleNoteFor,
  writtenToDisplayed,
} from '../../utils/transposition';
import Styles from './Flashcards.module.css';

const Flashcards = () => {
  const [isReady, setIsReady] = useState(false); // fades in once the valve images are loaded
  const [showOptions, setShowOptions] = useState(false);
  const [revealKeys, setRevealKeys] = useState(null); // correct fingering after a miss

  // Load options from storage
  const loadOptions = () => {
    const storedData = JSON.parse(localStorage.getItem('options'));
    if (storedData) return storedData;
    return {
      selectedInstrument: 'trumpet',
      selectedRange: 'custom',
      slider1Value: 0,
      slider2Value: notesWithoutFlats['trumpet'].length - 1,
      keyLetter: 'none',
      accidental: 'natural',
      mode: 'major',
      volume: 50,
      instrumentType: 'Bb',
      notation: 'written',
      euphoniumTrigger: false,
    };
  };

  const [selectedInstrument, setSelectedInstrument] = useState(loadOptions().selectedInstrument);
  const [selectedRange, setSelectedRange] = useState(loadOptions().selectedRange);
  const [slider1Value, setSlider1Value] = useState(loadOptions().slider1Value);
  const [slider2Value, setSlider2Value] = useState(loadOptions().slider2Value);
  const [keyLetter, setKeyLetter] = useState(loadOptions().keyLetter);
  const [accidental, setAccidental] = useState(loadOptions().accidental);
  const [mode, setMode] = useState(loadOptions().mode);
  const [allowRareNotes, setAllowRareNotes] = useState(!!loadOptions().allowRareNotes);
  const [instrumentType, setInstrumentType] = useState(loadOptions().instrumentType);
  const [euphoniumTrigger, setEuphoniumTrigger] = useState(!!loadOptions().euphoniumTrigger);
  const [notation, setNotation] = useState(() => resolveNotation(loadOptions()));

  // The saved type may belong to another instrument; fall back to its standard one
  const typeOf = (opts) => opts.instrumentType || getDefaultType(opts.selectedInstrument);

  // How a card's note is read (written, concert, or the original B♭-part reading).
  // Cards are always picked, fingered and graded as written pitch; this only
  // decides what is drawn, which key signature applies and which sample plays.
  const settingsOf = (opts) => ({
    instrument: opts.selectedInstrument,
    trumpetKey: typeOf(opts),
    notation: resolveNotation(opts),
  });

  // Rare spellings that read oddly on the staff
  const RARE_NOTES = ['Cb', 'Fb', 'B#', 'E#'];
  const isExcludedNote = (note) => RARE_NOTES.includes(note.substring(0, note.length - 1));

  // Every note index inside the chosen range, minus the rare spellings unless
  // the player turned them on in Options.
  const getCandidates = (min, max, instr, allowRare, type, settings) => {
    if (max < min) [min, max] = [max, min];

    // Translate index for "no flats" to "with flats"
    min = notes[instr].indexOf(notesWithoutFlats[instr][min]);
    max = notes[instr].indexOf(notesWithoutFlats[instr][max]);
    if (notes[instr][max].includes('#')) max++;

    const all = [];
    for (let i = min; i <= max; i++) all.push(i);
    // Only notes the chosen instrument type can actually play
    const playable = all.filter((i) =>
      getCombos(instr, notes[instr][i], fingeringType(instr, type, settings.notation)),
    );
    const base = playable.length > 0 ? playable : all;
    // Rare spellings are judged on what the player sees, so Concert Pitch can't put a C♭ on screen
    const filtered = allowRare
      ? base
      : base.filter((i) => !isExcludedNote(writtenToDisplayed(notes[instr][i], settings)));
    return filtered.length > 0 ? filtered : base;
  };

  const pickNoteIndex = (opts) => {
    const si = opts.selectedInstrument;
    const allowRare = !!opts.allowRareNotes;
    const settings = settingsOf(opts);
    const pool = getCandidates(
      opts.slider1Value,
      opts.slider2Value,
      si,
      allowRare,
      typeOf(opts),
      settings,
    );

    let keySignature = opts.keyLetter;
    if (opts.keyLetter !== 'none') {
      if (opts.accidental === 'sharp') keySignature += '#';
      else if (opts.accidental === 'flat') keySignature += 'b';
      if (opts.mode === 'minor') keySignature += 'm';
    }
    const inKey = (index) => {
      // The key signature is the one the player sees
      const name = writtenToDisplayed(notes[si][index], settings);
      return keySignatures[keySignature].includes(name.substring(0, name.length - 1));
    };
    const usable =
      opts.keyLetter !== 'none' && keySignature in keySignatures ? pool.filter(inKey) : pool;
    const list = usable.length > 0 ? usable : pool;
    return list[Math.floor(Math.random() * list.length)];
  };

  const [noteIndex, setNoteIndex] = useState(() => pickNoteIndex(loadOptions()));

  const changeNote = () => {
    // Read options fresh from storage each time to avoid using stale state
    setNoteIndex(pickNoteIndex(loadOptions()));
  };

  // New note whenever the options change
  useEffect(() => {
    changeNote();
  }, [selectedInstrument, selectedRange, slider1Value, slider2Value, keyLetter, accidental, mode, allowRareNotes, instrumentType, notation, euphoniumTrigger]);

  const toggleOptions = () => {
    if (!showOptions) {
      setShowOptions(true);
      return;
    }
    // Closing the panel: reload options from localStorage
    const opts = loadOptions();
    setSelectedInstrument(opts.selectedInstrument);
    setSelectedRange(opts.selectedRange);
    setSlider1Value(opts.slider1Value);
    setSlider2Value(opts.slider2Value);
    setKeyLetter(opts.keyLetter);
    setAccidental(opts.accidental);
    setMode(opts.mode);
    setAllowRareNotes(!!opts.allowRareNotes);
    setInstrumentType(opts.instrumentType);
    setNotation(resolveNotation(opts));
    setEuphoniumTrigger(!!opts.euphoniumTrigger);
    if (typeof opts.volume === 'number') setSetting('volume', opts.volume); // keeps the header slider in step
    setShowOptions(false);
  };

  const currentType = instrumentType || getDefaultType(selectedInstrument);
  const transposition = { instrument: selectedInstrument, trumpetKey: currentType, notation };
  const correctKeys = getCombos(
    selectedInstrument,
    notes[selectedInstrument][noteIndex],
    fingeringType(selectedInstrument, currentType, notation),
    euphoniumTrigger,
  );

  // Instrument calls this with true when a wrong answer lands, false 2s later
  const handleIncorrect = (showCorrectAnswer) => {
    if (!showCorrectAnswer) {
      setRevealKeys(null);
      return;
    }
    const combos = correctKeys || [];
    const first = combos[0];
    const keys =
      typeof first === 'string' ? first.split('') : Array.isArray(first) ? first.map(String) : [];
    // French horn is played with the left hand, so the index finger (the 'e'
    // key) is valve 1. Fingering numbers keys q-w-e as 1-2-3, so swap q and e
    // to print the numbers the way a horn player counts them.
    const flipped =
      selectedInstrument === 'frenchHorn'
        ? keys.map((key) => (key === 'q' ? 'e' : key === 'e' ? 'q' : key))
        : keys;
    setRevealKeys(flipped);
  };

  const handleAnswer = (isCorrect) => {
    const currentNote = notes[selectedInstrument][noteIndex];

    if (isCorrect) {
      awardCorrect(); // coins + the drop animation
      const safeNote = sampleNoteFor(currentNote, transposition).replace('#', 's');
      const sound = new Audio(`/sounds/${selectedInstrument}/${safeNote}.mp3`);
      sound.volume = getIdleState().settings.volume / 100;
      sound.load();
      sound.play();
    } else {
      recordWrong(); // feeds the accuracy stat
    }

    changeNote();
  };

  return (
    <main className={`${Styles.stage} ${isReady ? Styles.stageReady : ''}`}>
      <div className={Styles.toolbar}>
        <button
          type="button"
          className={`${Styles.optionsBtn} ${showOptions ? Styles.optionsBtnOpen : ''}`}
          onClick={toggleOptions}
          aria-label="Options"
        >
          <svg
            className={Styles.optionsIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="21" x2="14" y1="4" y2="4" />
            <line x1="10" x2="3" y1="4" y2="4" />
            <line x1="21" x2="12" y1="12" y2="12" />
            <line x1="8" x2="3" y1="12" y2="12" />
            <line x1="21" x2="16" y1="20" y2="20" />
            <line x1="12" x2="3" y1="20" y2="20" />
            <line x1="14" x2="14" y1="2" y2="6" />
            <line x1="8" x2="8" y1="10" y2="14" />
            <line x1="16" x2="16" y1="18" y2="22" />
          </svg>
          <span className={Styles.optionsLabel}>Options</span>
        </button>
      </div>

      <div className={Styles.noteArea}>
        <MusicNote
          note={writtenToDisplayed(notes[selectedInstrument][noteIndex], transposition)}
          selectedInstrument={selectedInstrument}
          incorrect={revealKeys !== null}
        />
      </div>

      <div className={Styles.lower}>
        <Fingering keys={revealKeys} />

        <div className={`${Styles.instrument} instrument`}>
          <Instrument
            correctKeys={correctKeys}
            handleIncorrect={handleIncorrect}
            handleAnswer={handleAnswer}
            instrument={selectedInstrument}
            handleLoading={(loading) => setIsReady(!loading)}
            mode={'Sandbox'}
            fourthValve={selectedInstrument === 'euphonium' && euphoniumTrigger}
          />
        </div>
      </div>

      <CoinDrops />

      {showOptions && <Options toggleOptions={toggleOptions} />}
    </main>
  );
};

export default Flashcards;